import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHead from "../components/PageHead";
import RunPipelineButton from "../components/RunPipelineButton";
import { actionStyle, confColor, fmt } from "../lib/signals";
import WinRateBadge from "../components/WinRateBadge";
import { TrendUp, TrendDown, PauseCircle, ChatCircleDots, Stack, ArrowUpRight } from "@phosphor-icons/react";

function Stat({ label, value, sub, icon: Icon, tint, testid }) {
  return (
    <div data-testid={testid} className="glass rounded-3xl p-5 fade-up">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${tint}`}>
          {Icon && <Icon size={17} weight="bold" />}
        </div>
      </div>
      <div className="font-mono font-bold text-3xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const s = stats || {};
  return (
    <>
      <PageHead title="Command Center" subtitle="Aggregated intelligence across all your desks"
        right={<RunPipelineButton onDone={load} />} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat testid="stat-total" label="Instruments" value={s.total_signals ?? "—"} sub={`${s.extracted_calls ?? 0} calls parsed`} icon={Stack} tint="bg-brand/10 text-brand" />
        <Stat testid="stat-long" label="Long / Buy" value={s.long ?? "—"} icon={TrendUp} tint="bg-bull/10 text-bull" />
        <Stat testid="stat-short" label="Short / Sell" value={s.short ?? "—"} icon={TrendDown} tint="bg-bear/10 text-bear" />
        <Stat testid="stat-hold" label="Hold / Watch" value={s.hold ?? "—"} icon={PauseCircle} tint="bg-neutral/10 text-neutral" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="stat-messages" label="Messages" value={s.messages ?? "—"} sub={`${s.unprocessed ?? 0} pending`} icon={ChatCircleDots} tint="bg-brand/10 text-brand" />
      </div>

      <div className="glass-strong rounded-3xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="font-display font-bold text-lg">Latest Signals</h2>
            <p className="text-xs text-muted-foreground">Newest calls first · analysis & levels per entry</p>
          </div>
          <button data-testid="view-all-hotlist" onClick={() => navigate("/hotlist")} className="text-sm text-brand hover:opacity-80 flex items-center gap-1 transition-opacity duration-150">
            Full hot list <ArrowUpRight size={14} weight="bold" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-semibold px-6 py-3">Ticker</th>
                <th className="text-left font-semibold px-3 py-3">Action</th>
                <th className="text-left font-semibold px-3 py-3">Src Win</th>
                <th className="text-right font-semibold px-3 py-3">Price</th>
                <th className="text-right font-semibold px-3 py-3">Entry</th>
                <th className="text-right font-semibold px-3 py-3">Stop</th>
                <th className="text-right font-semibold px-3 py-3">Target</th>
                <th className="text-right font-semibold px-3 py-3">Conv.</th>
                <th className="text-left font-semibold px-6 py-3">Timeframe</th>
              </tr>
            </thead>
            <tbody>
              {(s.top_conviction || []).map((h) => (
                <tr key={h.id} data-testid={`top-row-${h.ticker}`} onClick={() => navigate(`/signal/${h.id}`)}
                  className="border-b border-border/50 hover:bg-foreground/5 cursor-pointer transition-colors duration-150">
                  <td className="px-6 py-3 font-semibold whitespace-nowrap">{h.ticker} <span className="text-muted-foreground text-xs font-normal">{h.exchange}</span></td>
                  <td className="px-3 py-3"><span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold border ${actionStyle(h.action)}`}>{h.action}</span></td>
                  <td className="px-3 py-3"><WinRateBadge value={h.source_win_rate} /></td>
                  <td className="px-3 py-3 text-right font-mono">{fmt(h.price)}</td>
                  <td className="px-3 py-3 text-right font-mono text-muted-foreground">{fmt(h.levels?.entry)}</td>
                  <td className="px-3 py-3 text-right font-mono text-bear">{fmt(h.levels?.stop_loss)}</td>
                  <td className="px-3 py-3 text-right font-mono text-bull">{fmt(h.levels?.tp1)}</td>
                  <td className={`px-3 py-3 text-right font-mono font-bold ${confColor(h.confluence_score)}`}>{h.confluence_score}</td>
                  <td className="px-6 py-3 text-muted-foreground text-xs whitespace-nowrap">{h.timeframe}</td>
                </tr>
              ))}
              {stats && (!s.top_conviction || s.top_conviction.length === 0) && (
                <tr><td colSpan={9} className="px-6 py-12 text-center text-muted-foreground">No signals yet. Add a source in Integrations, then Run Pipeline.</td></tr>
              )}
              {!stats && <tr><td colSpan={9} className="px-6 py-12 text-center text-muted-foreground">Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
